import type {
  AtendimentoDoDia,
  BuscarCadastro,
  CadastroEncontrado,
  CategoriaComMotivos,
  ConversaDetalhe,
  ConversaResumo,
  DesempenhoDoDia,
  RespostaDeRevisao,
  DestinoUpload,
  EncerrarConversa,
  MensagemChat,
  MensagemRapidaResumo,
  RespostaPessoalForm,
  ResumoCaixaEntrada,
  SetorResumo,
  TransferirConversa,
} from '@mymaxx/shared';
import { api } from './api';

const base = '/v1/atendimento';

export interface AtendenteDoSetor {
  usuarioId: string;
  nome: string;
  papel: 'atendente' | 'supervisor';
  status: 'online' | 'pausa' | 'offline';
  motivoPausa: string | null;
  ativas: number;
  limite: number;
}

/** O que a tela de atendimento precisa saber sobre a configuração da operação. */
export interface CatalogosAtendimento {
  setores: SetorResumo[];
  motivos: CategoriaComMotivos[];
  mensagensRapidas: MensagemRapidaResumo[];
}

export const atendimentoApi = {
  /**
   * Os mesmos dados de /v1/gestao, numa rota que o atendente pode ler. As de gestão
   * exigem permissão de administração e devolviam 403 para quem só atende — era por isso
   * que o seletor de setor da transferência vinha vazio.
   */
  catalogos: () => api.get<CatalogosAtendimento>(`${base}/catalogos`),
  resumo: () => api.get<ResumoCaixaEntrada>(`${base}/resumo`),
  meuDia: () => api.get<AtendimentoDoDia[]>(`${base}/meu-dia`),

  // Respostas rápidas do próprio atendente — o roteiro dele, que o admin não gerencia.
  criarRespostaPessoal: (dados: RespostaPessoalForm) =>
    api.post<MensagemRapidaResumo>(`${base}/respostas-rapidas`, dados),
  editarRespostaPessoal: (id: string, dados: RespostaPessoalForm) =>
    api.put<MensagemRapidaResumo>(`${base}/respostas-rapidas/${id}`, dados),
  apagarRespostaPessoal: (id: string) => api.delete<void>(`${base}/respostas-rapidas/${id}`),
  minhas: () => api.get<ConversaResumo[]>(`${base}/minhas`),
  fila: (setorId?: string) =>
    api.get<ConversaResumo[]>(`${base}/fila${setorId ? `?setorId=${setorId}` : ''}`),
  emAndamento: () => api.get<ConversaResumo[]>(`${base}/em-andamento`),
  definirDespedida: (texto: string) =>
    api.put<{ despedida: string | null }>(`${base}/despedida`, { texto }),
  chavePush: () => api.get<{ chave: string }>(`${base}/push/chave`),
  inscreverPush: (inscricao: { endpoint: string; p256dh: string; auth: string }) =>
    api.post<void>(`${base}/push/inscrever`, inscricao),
  cancelarPush: (endpoint: string) => api.post<void>(`${base}/push/cancelar`, { endpoint }),
  encerradas: (busca?: string) =>
    api.get<ConversaResumo[]>(
      `${base}/encerradas${busca ? `?busca=${encodeURIComponent(busca)}` : ''}`,
    ),
  detalhe: (id: string) => api.get<ConversaDetalhe>(`${base}/${id}`),
  /** Protocolo anterior do mesmo cliente, aberto a partir do atendimento em andamento. */
  anterior: (id: string, anteriorId: string) =>
    api.get<ConversaDetalhe>(`${base}/${id}/anteriores/${anteriorId}`),

  puxarProxima: (setorId?: string) =>
    api.post<ConversaDetalhe>(`${base}/puxar${setorId ? `?setorId=${setorId}` : ''}`),
  assumir: (id: string) => api.post<ConversaDetalhe>(`${base}/${id}/assumir`),
  /** Entra junto com quem já está atendendo, sem tirar ninguém. */
  participar: (id: string) => api.post<ConversaDetalhe>(`${base}/${id}/participar`),

  /** Procura o cadastro no ERP para conferir antes de vincular. */
  buscarCadastro: (dados: BuscarCadastro) =>
    api.post<CadastroEncontrado | null>(`${base}/clientes/buscar`, dados),
  /** Liga o atendimento ao cadastro encontrado. */
  vincularCadastro: (id: string, dados: BuscarCadastro) =>
    api.post<ConversaDetalhe>(`${base}/${id}/vincular`, dados),

  responder: (id: string, texto: string, respondeAId?: string) =>
    api.post<MensagemChat>(`${base}/${id}/mensagens`, {
      texto,
      ...(respondeAId ? { respondeAId } : {}),
    }),
  anotar: (id: string, texto: string) => api.post<MensagemChat>(`${base}/${id}/notas`, { texto }),

  transferir: (id: string, dados: TransferirConversa) =>
    api.post<{ ok: true }>(`${base}/${id}/transferir`, dados),
  encerrar: (id: string, dados: EncerrarConversa) =>
    api.post<{ ok: true }>(`${base}/${id}/encerrar`, dados),

  definirPresenca: (status: 'online' | 'pausa' | 'offline', motivo?: string) =>
    api.post<{ ok: true }>(`${base}/presenca`, { status, motivo: motivo ?? '' }),
  sinalDePresenca: () => api.post<void>(`${base}/presenca/sinal`),
  /** Revisa o texto antes de enviar. Propõe; quem decide é quem escreveu. */
  revisar: (texto: string) => api.post<RespostaDeRevisao>(`${base}/revisao`, { texto }),

  /** Aparecer, ou não, na conversa interna. Não mexe no plantão. */
  definirInvisivel: (invisivel: boolean) =>
    api.post<void>(`${base}/presenca/invisivel`, { invisivel }),
  /** Como estou indo hoje: encerrados e nota média. */
  desempenho: () => api.get<DesempenhoDoDia>(`${base}/desempenho`),
  equipeDoSetor: (setorId: string) =>
    api.get<AtendenteDoSetor[]>(`${base}/setores/${setorId}/equipe`),

  /** Liga/desliga a fila automática do setor (admin ou supervisor dele). */
  definirDistribuicao: (setorId: string, automatica: boolean) =>
    api.post<{ ok: true; distribuicaoAutomatica: boolean }>(
      `${base}/setores/${setorId}/distribuicao`,
      { automatica },
    ),

  /** Anexo do atendente: pede a URL, envia direto ao storage e confirma. */
  async enviarArquivo(id: string, arquivo: File, legenda?: string): Promise<MensagemChat> {
    const destino = await api.post<DestinoUpload>(`${base}/${id}/anexos/preparar`, {
      nome: arquivo.name,
      mimeType: arquivo.type,
      tamanho: arquivo.size,
    });
    const envio = await fetch(destino.url, {
      method: 'PUT',
      body: arquivo,
      headers: { 'Content-Type': arquivo.type },
    });
    if (!envio.ok) throw new Error('Não consegui enviar o arquivo.');
    return api.post<MensagemChat>(`${base}/${id}/anexos`, {
      chave: destino.chave,
      nome: arquivo.name,
      mimeType: arquivo.type,
      legenda: legenda ?? '',
    });
  },
};
